import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";

function BankDetails({ user }) {
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Verification Form State
  const [showEditForm, setShowEditForm] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Form Fields
  const [accountHolderName, setAccountHolderName] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [showAcc, setShowAcc] = useState(false);
  const [showConfirmAcc, setShowConfirmAcc] = useState(false);
  const [accountType, setAccountType] = useState("current");
  const [upiId, setUpiId] = useState("");

  // IFSC Lookup State
  const [ifscLoading, setIfscLoading] = useState(false);
  const [ifscError, setIfscError] = useState("");
  const [ifscSuccess, setIfscSuccess] = useState(null);

  const fetchBank = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/vendor/bank");
      const bankData = res.data.bank || null;
      setBank(bankData);

      if (bankData) {
        setAccountHolderName(
          bankData.accountHolderName ||
            `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
        );
        setBankName(bankData.bankName || "");
        setIfsc(bankData.ifsc || "");
        setAccountType(bankData.accountType || "current");
        setUpiId(bankData.upiId || "");
      } else {
        setAccountHolderName(
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim()
        );
      }
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load bank details.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchBank();
  }, [fetchBank]);

  // Live IFSC Lookup
  const handleIfscChange = async (val) => {
    const clean = val.toUpperCase().trim();
    setIfsc(clean);
    setIfscError("");
    setIfscSuccess(null);

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (clean.length === 11) {
      if (!ifscRegex.test(clean)) {
        setIfscError("Invalid IFSC format. Must be 4 letters, 0, and 6 alphanumeric characters.");
        return;
      }

      try {
        setIfscLoading(true);
        const res = await api.get(`/vendor/bank/ifsc/${clean}`);
        if (res.data && res.data.success) {
          setBankName(res.data.bankName || "");
          setIfscSuccess({
            bankName: res.data.bankName,
            branch: res.data.branch,
            city: res.data.city,
            state: res.data.state,
          });
        }
      } catch (err) {
        setBankName("");
        setIfscError(
          err.response?.data?.msg || "Invalid IFSC code. Bank branch not found."
        );
      } finally {
        setIfscLoading(false);
      }
    } else {
      setBankName("");
    }
  };

  // Submit Real Bank Verification
  const handleVerifyBank = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!accountHolderName.trim()) {
      setError("Please enter the Account Holder Name as registered in the bank.");
      return;
    }
    if (!ifsc.trim() || ifsc.length !== 11) {
      setError("Please enter a valid 11-character IFSC code.");
      return;
    }
    if (!bankName.trim()) {
      setError("Bank name could not be resolved from IFSC. Please check your IFSC code.");
      return;
    }
    if (!accountNumber.trim() || accountNumber.length < 8) {
      setError("Please enter a valid Bank Account Number (at least 8 digits).");
      return;
    }
    if (accountNumber !== confirmAccountNumber) {
      setError("Account numbers do not match. Please verify both fields.");
      return;
    }

    try {
      setVerifying(true);
      const res = await api.post("/vendor/bank/verify", {
        accountHolderName: accountHolderName.trim(),
        accountNumber: accountNumber.trim(),
        confirmAccountNumber: confirmAccountNumber.trim(),
        ifsc: ifsc.trim().toUpperCase(),
        accountType,
        upiId: upiId.trim(),
      });

      if (res.data.verified) {
        setSuccess(res.data.message || "✓ Bank account verified successfully!");
        setBank(res.data.bank);
        setShowEditForm(false);
        setAccountNumber("");
        setConfirmAccountNumber("");
      } else if (res.data.status === "under_review") {
        setSuccess(res.data.message || "Bank details submitted and queued for review.");
        setBank(res.data.bank);
        setShowEditForm(false);
      } else {
        setSuccess(res.data.message || "Bank details updated.");
        setBank(res.data.bank);
        setShowEditForm(false);
      }

      await fetchBank();
    } catch (err) {
      const msg =
        err.response?.data?.msg ||
        err.response?.data?.error ||
        err.message ||
        "Bank account verification failed. Please check the details.";
      setError(msg);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="skeleton" style={{ height: "32px", width: "240px", marginBottom: "8px" }} />
        <div className="skeleton" style={{ height: "16px", width: "320px", marginBottom: "24px" }} />
        <div className="skeleton" style={{ height: "260px", borderRadius: "12px" }} />
      </div>
    );
  }

  const isVerified = bank && bank.verificationStatus === "verified";
  const isUnderReview = bank && bank.verificationStatus === "under_review";
  const isRejected = bank && bank.verificationStatus === "rejected";
  const hasAccount = bank && !!bank.accountNumberMasked;

  const accountsMatch =
    accountNumber && confirmAccountNumber && accountNumber === confirmAccountNumber;
  const accountsMismatch =
    accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber;

  return (
    <div>
      {/* Page Header */}
      <div
        className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4"
        style={{ paddingBottom: "20px", borderBottom: "1px solid var(--border)" }}
      >
        <div>
          <h2 style={{ fontSize: "22px", fontWeight: "900", color: "var(--dark)", margin: "0 0 4px" }}>
            Bank Payout &amp; Settlement Account
          </h2>
          <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
            Real-time verified bank account for automated marketplace settlement payouts and disbursements.
          </p>
        </div>

        {!showEditForm && hasAccount && (
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => {
              setShowEditForm(true);
              setError("");
              setSuccess("");
            }}
            style={{ padding: "9px 20px", fontSize: "13px", fontWeight: "800" }}
          >
            Change Settlement Account
          </button>
        )}
      </div>

      {/* Notifications */}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center justify-content-between mb-4"
          style={{ borderRadius: "10px", padding: "14px 18px", fontSize: "14px" }}
        >
          <div>
            <strong>Verification Error: </strong>
            <span>{error}</span>
          </div>
          <button type="button" className="btn-close" onClick={() => setError("")} />
        </div>
      )}

      {success && (
        <div
          className="alert alert-success d-flex align-items-center justify-content-between mb-4"
          style={{ borderRadius: "10px", padding: "14px 18px", fontSize: "14px" }}
        >
          <div>
            <strong>Success: </strong>
            <span>{success}</span>
          </div>
          <button type="button" className="btn-close" onClick={() => setSuccess("")} />
        </div>
      )}

      {/* Settlement Protection Banner */}
      {isVerified ? (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <span style={{ fontSize: "22px" }}>🛡️</span>
          <div>
            <strong style={{ color: "#166534", fontSize: "14px", display: "block" }}>
              Settlement Payouts Active &amp; Protected
            </strong>
            <span style={{ fontSize: "12.5px", color: "#15803d" }}>
              Your bank account is verified with Cashfree SecureID. Marketplace order earnings will be directly transferred to this settlement account.
            </span>
          </div>
        </div>
      ) : isRejected ? (
        <div
          style={{
            background: "#fee2e2",
            border: "1.5px solid #fecaca",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <span style={{ fontSize: "24px" }}>✕</span>
            <div>
              <strong style={{ color: "#991b1b", fontSize: "14.5px", display: "block" }}>
                Bank Account Verification Rejected
              </strong>
              <span style={{ fontSize: "13px", color: "#b91c1c" }}>
                Reason: {bank?.rejectionReason || "Bank account verification failed. Please correct your details and submit again."}
              </span>
            </div>
          </div>
          {!showEditForm && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowEditForm(true);
                setError("");
                setSuccess("");
              }}
              style={{ fontSize: "12.5px", padding: "8px 18px", fontWeight: "700" }}
            >
              Update Bank Details &rarr;
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: "12px",
            padding: "16px 20px",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <span style={{ fontSize: "22px" }}>⚠️</span>
          <div>
            <strong style={{ color: "#b45309", fontSize: "14px", display: "block" }}>
              Bank Account Verification Required
            </strong>
            <span style={{ fontSize: "12.5px", color: "#92400e" }}>
              To prevent fraudulent accounts and enable direct merchant settlements, please verify your bank account below. Payouts remain locked until verification is complete.
            </span>
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Main Column */}
        <div className="col-lg-7">
          {/* ═══════════ 1. VERIFIED ACCOUNT DISPLAY CARD ═══════════ */}
          {!showEditForm && hasAccount ? (
            <div
              className="stat-card"
              style={{
                background: "#ffffff",
                border: "1.5px solid #e0e4ea",
                borderRadius: "14px",
                padding: "26px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "20px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                  <div
                    style={{
                      width: "52px",
                      height: "52px",
                      borderRadius: "12px",
                      background: isVerified ? "#dcfce7" : "#fef3c7",
                      color: isVerified ? "#166534" : "#b45309",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                    }}
                  >
                    🏦
                  </div>
                  <div>
                    <h3 style={{ fontSize: "20px", fontWeight: "900", color: "var(--dark)", margin: 0 }}>
                      {bank.bankName}
                    </h3>
                    <span style={{ fontSize: "12.5px", color: "var(--text-muted)", marginTop: "2px", display: "block" }}>
                      {bank.branchName ? `${bank.branchName} Branch &middot; ` : ""}
                      {bank.accountType === "savings" ? "Savings Account" : "Current Account (Business)"}
                    </span>
                  </div>
                </div>

                <span
                  style={{
                    background: isVerified ? "#dcfce7" : isUnderReview ? "#fef3c7" : "#fee2e2",
                    color: isVerified ? "#166534" : isUnderReview ? "#b45309" : "#991b1b",
                    padding: "6px 14px",
                    borderRadius: "99px",
                    fontSize: "12px",
                    fontWeight: "800",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <span
                    style={{
                      width: "7px",
                      height: "7px",
                      borderRadius: "50%",
                      background: isVerified ? "#16a34a" : isUnderReview ? "#f59e0b" : "#dc2626",
                    }}
                  />
                  {isVerified
                    ? "✓ Bank Account Verified"
                    : isUnderReview
                    ? "● Verification In Review"
                    : "✕ Action Required"}
                </span>
              </div>

              {/* Grid of Bank Details */}
              <div
                className="bank-details-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "16px",
                  background: "#fbfaf8",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #edf0f4",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>
                    Account Holder
                  </div>
                  <div style={{ fontSize: "15px", fontWeight: "800", color: "var(--dark)", marginTop: "3px" }}>
                    {bank.nameAtBank || bank.accountHolderName}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>
                    Account Number (Masked)
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "800",
                      color: "var(--dark)",
                      fontFamily: "monospace",
                      marginTop: "3px",
                    }}
                  >
                    {bank.accountNumberMasked}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>
                    IFSC Code
                  </div>
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "800",
                      color: "var(--dark)",
                      fontFamily: "monospace",
                      marginTop: "3px",
                    }}
                  >
                    {bank.ifsc}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: "800", textTransform: "uppercase" }}>
                    UPI ID
                  </div>
                  <div style={{ fontSize: "14px", fontWeight: "700", color: "var(--dark)", marginTop: "3px" }}>
                    {bank.upiId || "Not Linked"}
                  </div>
                </div>
              </div>

              {/* Verification Metadata Footer */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "12px",
                  color: "var(--text-muted)",
                  borderTop: "1px solid #f3f4f6",
                  paddingTop: "14px",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <span>
                  🔒 Verified via <strong>Cashfree SecureID</strong>
                  {bank.verifiedAt && ` on ${new Date(bank.verifiedAt).toLocaleDateString("en-IN")}`}
                </span>

                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    setShowEditForm(true);
                    setError("");
                    setSuccess("");
                  }}
                  style={{ fontWeight: "700" }}
                >
                  Change Account
                </button>
              </div>
            </div>
          ) : (
            /* ═══════════ 2. REAL VERIFICATION INPUT FORM ═══════════ */
            <div
              className="card-brand"
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "14px",
                padding: "26px",
              }}
            >
              <div style={{ marginBottom: "22px" }}>
                <h4 style={{ fontSize: "17px", fontWeight: "900", color: "var(--dark)", margin: "0 0 4px" }}>
                  {hasAccount ? "Update & Re-Verify Bank Account" : "Link & Verify Settlement Bank Account"}
                </h4>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: 0 }}>
                  Enter your IFSC code and account number to run instant gateway verification.
                </p>
              </div>

              <form onSubmit={handleVerifyBank}>
                {/* STEP 1: Account Holder Name */}
                <div style={{ marginBottom: "18px" }}>
                  <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                    1. Account Holder Name <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    required
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                    placeholder="Full name as registered in bank passbook"
                    style={{ padding: "10px 14px", fontSize: "14px" }}
                  />
                  <div style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "4px" }}>
                    Must match your business registration or seller profile name.
                  </div>
                </div>

                {/* STEP 2: IFSC Code with Auto-Lookup */}
                <div style={{ marginBottom: "18px" }}>
                  <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                    2. IFSC Code <span style={{ color: "var(--primary)" }}>*</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      className="form-control"
                      required
                      maxLength={11}
                      value={ifsc}
                      onChange={(e) => handleIfscChange(e.target.value)}
                      placeholder="e.g. HDFC0001234, SBIN0000456"
                      style={{
                        padding: "10px 14px",
                        fontSize: "14px",
                        fontFamily: "monospace",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                      }}
                    />
                    {ifscLoading && (
                      <span
                        className="spinner-border spinner-border-sm text-primary"
                        style={{ position: "absolute", right: "14px", top: "14px" }}
                      />
                    )}
                  </div>

                  {ifscError && (
                    <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginTop: "5px" }}>
                      ✕ {ifscError}
                    </div>
                  )}

                  {ifscSuccess && (
                    <div
                      style={{
                        fontSize: "12.5px",
                        color: "#166534",
                        fontWeight: "700",
                        marginTop: "6px",
                        background: "#f0fdf4",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      ✓ {ifscSuccess.bankName} &middot; {ifscSuccess.branch} ({ifscSuccess.city}, {ifscSuccess.state})
                    </div>
                  )}
                </div>

                {/* STEP 3: Auto-Populated Bank Name (LOCKED / READ-ONLY) */}
                <div style={{ marginBottom: "18px" }}>
                  <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                    Bank Name (Auto-Identified) <span style={{ color: "var(--text-muted)" }}>[Locked]</span>
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      disabled
                      value={bankName || "Automatically identified from IFSC code"}
                      style={{
                        padding: "10px 14px",
                        fontSize: "14px",
                        fontWeight: "700",
                        background: "#f3f4f6",
                        color: bankName ? "var(--dark)" : "#9ca3af",
                        cursor: "not-allowed",
                      }}
                    />
                    {bankName && (
                      <span
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "10px",
                          fontSize: "11px",
                          fontWeight: "800",
                          color: "#166534",
                          background: "#dcfce7",
                          padding: "3px 8px",
                          borderRadius: "6px",
                        }}
                      >
                        ✓ Verified Bank
                      </span>
                    )}
                  </div>
                </div>

                {/* STEP 4 & 5: Bank Account Number & Confirm Account Number */}
                <div className="row g-3" style={{ marginBottom: "18px" }}>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                      3. Bank Account Number <span style={{ color: "var(--primary)" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showAcc ? "text" : "password"}
                        className="form-control"
                        required
                        value={accountNumber}
                        onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="Account number"
                        style={{ padding: "10px 40px 10px 14px", fontSize: "14px", fontFamily: showAcc ? "monospace" : "inherit" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowAcc(!showAcc)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          fontSize: "15px",
                          cursor: "pointer",
                          padding: "4px"
                        }}
                        title={showAcc ? "Hide account number" : "Show account number"}
                      >
                        {showAcc ? "👁️" : "👁️‍🗨️"}
                      </button>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                      4. Confirm Account Number <span style={{ color: "var(--primary)" }}>*</span>
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type={showConfirmAcc ? "text" : "password"}
                        className="form-control"
                        required
                        value={confirmAccountNumber}
                        onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ""))}
                        placeholder="Re-enter account number"
                        style={{ padding: "10px 40px 10px 14px", fontSize: "14px", fontFamily: showConfirmAcc ? "monospace" : "inherit" }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmAcc(!showConfirmAcc)}
                        style={{
                          position: "absolute",
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          color: "#64748b",
                          fontSize: "15px",
                          cursor: "pointer",
                          padding: "4px"
                        }}
                        title={showConfirmAcc ? "Hide account number" : "Show account number"}
                      >
                        {showConfirmAcc ? "👁️" : "👁️‍🗨️"}
                      </button>
                    </div>
                  </div>
                </div>

                {accountsMatch && (
                  <div style={{ fontSize: "12px", color: "#166534", fontWeight: "700", marginBottom: "14px" }}>
                    ✓ Account numbers match
                  </div>
                )}
                {accountsMismatch && (
                  <div style={{ fontSize: "12px", color: "#dc2626", fontWeight: "700", marginBottom: "14px" }}>
                    ✕ Account numbers do not match
                  </div>
                )}

                {/* Account Type & UPI ID */}
                <div className="row g-3" style={{ marginBottom: "22px" }}>
                  <div className="col-md-6">
                    <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                      Account Type
                    </label>
                    <select
                      className="form-select"
                      value={accountType}
                      onChange={(e) => setAccountType(e.target.value)}
                      style={{ padding: "10px 14px", fontSize: "14px" }}
                    >
                      <option value="current">Current Account (Business)</option>
                      <option value="savings">Savings Account (Personal)</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label" style={{ fontWeight: "800", fontSize: "13px" }}>
                      UPI ID (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={upiId}
                      onChange={(e) => setUpiId(e.target.value.trim())}
                      placeholder="merchant@okhdfcbank"
                      style={{ padding: "10px 14px", fontSize: "14px" }}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  {hasAccount && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowEditForm(false)}
                      disabled={verifying}
                      style={{ padding: "11px 20px", fontWeight: "700" }}
                    >
                      Cancel
                    </button>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary flex-grow-1"
                    disabled={verifying || !bankName || !accountsMatch}
                    style={{
                      padding: "12px 24px",
                      fontSize: "14px",
                      fontWeight: "800",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    {verifying ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Verifying with Cashfree Bank Gateway...
                      </>
                    ) : (
                      "🔒 Verify & Save Bank Account"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Sidebar Info Column */}
        <div className="col-lg-5">
          <div
            className="stat-card"
            style={{
              background: "#ffffff",
              border: "1.5px solid #e0e4ea",
              borderRadius: "14px",
              padding: "24px",
              marginBottom: "20px",
            }}
          >
            <h4 style={{ fontSize: "16px", fontWeight: "900", color: "var(--dark)", marginBottom: "14px" }}>
              Settlement Security &amp; FAQ
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", fontSize: "13px" }}>
              <div>
                <strong style={{ color: "var(--dark)", display: "block", marginBottom: "3px" }}>
                  How does bank verification work?
                </strong>
                <p style={{ color: "var(--text-muted)", margin: 0, lineHeight: "1.5" }}>
                  We communicate directly with the bank via Cashfree SecureID to confirm that the account is active and the account holder name matches your seller profile.
                </p>
              </div>

              <div>
                <strong style={{ color: "var(--dark)", display: "block", marginBottom: "3px" }}>
                  Why is my bank name locked?
                </strong>
                <p style={{ color: "var(--text-muted)", margin: 0, lineHeight: "1.5" }}>
                  The bank and branch name are verified directly from RBI records based on your 11-digit IFSC code to prevent bank mismatch errors.
                </p>
              </div>

              <div>
                <strong style={{ color: "var(--dark)", display: "block", marginBottom: "3px" }}>
                  When will I receive payouts?
                </strong>
                <p style={{ color: "var(--text-muted)", margin: 0, lineHeight: "1.5" }}>
                  Once verified, customer order earnings are automatically settled according to your seller plan payout cycle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BankDetails;
